import { tokenize } from "./normalize";

/**
 * Kiadás ↔ beszerzési tétel párosítás. Cél: ha rögzítesz egy „XPS 5 cm"
 * kiadást, a lista magától `delivered` állapotba lépjen. Konzervatív: inkább
 * ne párosítson, mint hogy rosszat jelöljön meg.
 */

/** Ezek a szavak minden építési szövegben előfordulnak, nem azonosítanak semmit. */
const STOPWORDS = new Set([
  "es",
  "vagy",
  "db",
  "kg",
  "m",
  "m2",
  "m3",
  "cm",
  "mm",
  "ml",
  "tabla",
  "szal",
  "raklap",
  "csomag",
  "keszlet",
  "anyag",
  "beszerzes",
  "vasarlas",
  "szamla",
  "a",
  "az"
]);

export function significantTokens(value: string): string[] {
  return tokenize(value).filter((t) => t.length >= 3 && !STOPWORDS.has(t));
}

export const MATCH_THRESHOLD = 0.6;

/**
 * Ragozás elnyelése, összetett szó nélkül: „betonvasat" ~ „betonvas" igen,
 * de „betonpumpa" ~ „beton" nem — az utóbbi már másik termék.
 */
const MAX_SUFFIX_GROWTH = 3;

function tokenHit(token: string, titleTokens: Set<string>): number {
  if (titleTokens.has(token)) return 1;
  for (const candidate of titleTokens) {
    const [short, long] =
      token.length <= candidate.length ? [token, candidate] : [candidate, token];
    if (short.length < 4) continue;
    if (long.length - short.length > MAX_SUFFIX_GROWTH) continue;
    if (long.startsWith(short)) return 0.9;
  }
  return 0;
}

/**
 * 0..1 pontszám: a tétel jellemző szavainak mekkora része szerepel a kiadás
 * címében.
 */
export function scoreMaterialMatch(itemName: string, expenseTitle: string): number {
  const itemTokens = significantTokens(itemName);
  if (itemTokens.length === 0) return 0;
  const titleTokens = new Set(significantTokens(expenseTitle));
  if (titleTokens.size === 0) return 0;

  const hit = itemTokens.reduce((acc, token) => acc + tokenHit(token, titleTokens), 0);
  return Math.min(1, hit / itemTokens.length);
}

export type ProcurementMatchInput = { id: string; name: string; status: string };

export type ProcurementMatch = { id: string; score: number };

/** Egy kiadáshoz illeszkedő, még nem beszerzett tételek — a legjobb elöl. */
export function matchProcurementItems(
  expenseTitle: string,
  items: ProcurementMatchInput[],
  threshold = MATCH_THRESHOLD
): ProcurementMatch[] {
  return items
    .filter((i) => i.status === "needed" || i.status === "quoted" || i.status === "ordered")
    .map((i) => ({ id: i.id, score: scoreMaterialMatch(i.name, expenseTitle) }))
    .filter((m) => m.score >= threshold)
    .sort((a, b) => b.score - a.score);
}

/** Alapértelmezett átfutási idő, ha nincs rögzített tény a beszállítóról. */
const DEFAULT_LEAD_TIMES: Array<{ keywords: string[]; days: number }> = [
  { keywords: ["xps", "eps", "szigetel", "kzs"], days: 14 },
  { keywords: ["ablak", "ajto", "nyilaszaro"], days: 42 },
  { keywords: ["cserep", "terran", "tetofolia"], days: 21 },
  { keywords: ["termobrik", "tegla", "blokk", "zsaluko"], days: 14 },
  { keywords: ["betonvas", "betonacel", "vasalas", "acel"], days: 10 },
  { keywords: ["beton", "mixer", "betonpumpa"], days: 3 },
  { keywords: ["homok", "kavics", "zuzott", "soder"], days: 3 },
  { keywords: ["hoszivattyu", "gepeszet", "kazan"], days: 30 }
];

export function defaultLeadTimeDays(name: string): number | null {
  const tokens = new Set(tokenize(name));
  for (const rule of DEFAULT_LEAD_TIMES) {
    if (rule.keywords.some((k) => tokens.has(k) || tokenize(name).join(" ").includes(k))) {
      return rule.days;
    }
  }
  return null;
}
